/*
* Palo Alto Theme
*
* Use this file to add custom Javascript to Palo Alto.  Keeping your custom
* Javascript in this fill will make it easier to update Palo Alto. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/


(function() {
  // Add custom code below this line


  function fetchProduct(handle) {
    return fetch(`${window.theme.routes.root}products/${handle}.js`)
      .then(function(response) {
        return response.json();
      })
      .catch(function() {
        return null;
      });
  }

  /**
   * Constrain a Shopify CDN image URL to a max width.
   * Shopify supports appending ?width=N or inserting _{W}x before the extension.
   * We use the query-param approach which works for all CDN image URLs.
   * @param {string} url  - Raw Shopify CDN image URL
   * @param {number} width - Desired max width in px
   * @returns {string}
   */
  function constrainImageUrl(url, width) {
    if (!url || !width) return url;
    try {
      const u = new URL(url);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (_) {
      // fallback: append as query string if URL is relative/malformed
      const separator = url.includes('?') ? '&' : '?';
      return url + separator + 'width=' + width;
    }
  }

  class ProductGridItemLinked extends HTMLElement {
    constructor() {
      super();
      this.productDataCache = {};
      this.productRequests = {};
      this.hoverMediaObserver = null;
      this.initialized = false;
    }

    connectedCallback() {
      requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      this.initialized = true;

      this.swatches = this.querySelectorAll('.js-product-grid-item-linked-swatch');
      this.colorLabelText = this.querySelector('.js-product-grid-item-color-label-text');
      this.productItem = this.closest('.product-grid-item');
      if (!this.productItem) return;

      this.productPrice = this.productItem.querySelector('.product-grid-item__price');
      this.productTitle = this.productItem.querySelector('.product-grid-item__title');
      this.productMediaHolder = this.productItem.querySelector('.product-media__holder');
      this.productImage = this.productItem.querySelector('[data-product-image] img');
      this.quickViewBtn = this.productItem.querySelector('[data-button-quick-view]');
      this.swiperEl = this.productItem.querySelector('.js-product-grid-item-linked-swatches');
      this.moreButton = this.productItem.querySelector('.js-product-grid-item-linked-swatch-more');
      this.activeSwatch = this.querySelector('.js-product-grid-item-linked-swatch.selected') || null;
      this.moreButton?.addEventListener('click', () => this.toggleSwatches());
      this.addEventListener('mouseover', (event) => {
        const swatch = event.target.closest('.js-product-grid-item-linked-swatch');
        if (!swatch || !this.contains(swatch) || swatch === this.activeSwatch) return;
        this.activeSwatch = swatch;
        this.applySwatchState(swatch);
      });

      this.addEventListener('focusin', (event) => {
        const swatch = event.target.closest('.js-product-grid-item-linked-swatch');
        if (!swatch || !this.contains(swatch) || swatch === this.activeSwatch) return;
        this.activeSwatch = swatch;
        this.applySwatchState(swatch);
      });

      this.addEventListener('click', (event) => {
        const swatch = event.target.closest('.js-product-grid-item-linked-swatch');
        if (!swatch || !this.contains(swatch)) return;
        event.preventDefault();
        this.activeSwatch = swatch;
        this.applySwatchState(swatch);
      });

      if (this.productMediaHolder) {
        this.productMediaHolder.addEventListener('mouseenter', () => {
          this.syncActiveSwatchMedia();
          requestAnimationFrame(() => this.syncActiveSwatchMedia());
        });
      }
      this.applyActiveCollectionColor();
    }

    toggleSwatches() {
      if (!this.swiperEl) return;

      const isExpanded = this.swiperEl.classList.toggle('is-expanded');
      this.moreButton?.setAttribute('aria-expanded', String(isExpanded));
      this.moreButton?.setAttribute('aria-label', isExpanded ? 'Show fewer colors' : 'Show more colors');
    }

    applyActiveCollectionColor() {
      const collection = this.closest('[data-section-type="collection-template"]');
      if (!collection) return;

      const activeColors = Array.from(
        collection.querySelectorAll('[data-color-filter] .filter__input:checked')
      )
        .map((input) => this.normalizeColor(input.dataset.filterValueLabel))
        .filter(Boolean);

      if (!activeColors.length) {
        this.productItem.hidden = false;
        return;
      }

      const matchingSwatch = Array.from(this.swatches).find((swatch) => {
        return activeColors.includes(this.normalizeColor(swatch.dataset.colorLabel));
      });

      // Shopify's broad colour taxonomy can return Khaki/Dark Khaki for a
      // Brown filter.  Keep a card only when its linked colours contain the
      // exact selected label; otherwise it is not an exact Brown result.
      if (!matchingSwatch) {
        this.productItem.hidden = true;
        return;
      }

      // Make the exact matching colour both visible and selected, rather than
      // leaving the card on the linked product's default colour.
      this.productItem.hidden = false;
      if (this.swiperEl && matchingSwatch !== this.swiperEl.firstElementChild) {
        this.swiperEl.insertBefore(matchingSwatch, this.swiperEl.firstElementChild);
      }
      this.applySwatchState(matchingSwatch);
    }

    normalizeColor(value) {
      return (value || '').trim().replace(/\s+/g, ' ').toLowerCase();
    }

    applySwatchState(swatch) {
      if (!swatch) return;
      this.activeSwatch = swatch;
      const featuredImage = swatch.getAttribute('data-featured-image');
      const secondaryImage = swatch.getAttribute('data-secondary-image') || featuredImage;

      this.updateSelectedSwatch(swatch);
      this.updateColorLabelText(swatch.getAttribute('data-color-label'));
      this.updateProductPrice(swatch.getAttribute('data-price'));
      this.updateProductTitle(swatch.getAttribute('data-title'));
      this.updateProductLink(swatch.getAttribute('href'));
      this.updateProductImage(featuredImage);
      this.updateProductHoverImage(secondaryImage);
      this.updateQuickViewBtn(swatch.getAttribute('data-handle'));
      this.syncSwatchMediaFromHandle(swatch);
    }

    syncSwatchMediaFromHandle(swatch) {
      const handle = swatch.getAttribute('data-handle');
      if (!handle) return;

      if (this.productDataCache[handle]) {
        this.applyProductDataMedia(this.productDataCache[handle]);
        return;
      }
      this.productRequests[handle] ||= fetchProduct(handle);
      this.productRequests[handle].then((productData) => {
        if (!productData || !this.activeSwatch || this.activeSwatch.getAttribute('data-handle') !== handle) return;
        this.productDataCache[handle] = productData;
        this.applyProductDataMedia(productData);
      });
    }

    applyProductDataMedia(productData) {
      const images = productData.images || [];
      const featuredImage = productData.featured_image || images[0] || '';
      const secondaryImage = images[1] || featuredImage;

      if (featuredImage) this.updateProductImage(constrainImageUrl(featuredImage, 800));
      if (secondaryImage) this.updateProductHoverImage(constrainImageUrl(secondaryImage, 800));
    }

    syncActiveSwatchMedia() {
      const swatch = this.activeSwatch || this.querySelector('.js-product-grid-item-linked-swatch.selected');
      if (!swatch) return;
      const cached = this.productDataCache[swatch.dataset.handle];
      if (cached) {
        this.applyProductDataMedia(cached);
        return;
      }
      this.syncSwatchMediaFromHandle(swatch);
    }

    updateSelectedSwatch(swatch) {
      this.swatches.forEach((s) => { s.classList.remove('selected'); });
      swatch.classList.add('selected');
    }

    updateColorLabelText(colorLabel) {
      if (!this.colorLabelText) return;
      this.colorLabelText.textContent = colorLabel;
    }

    updateProductPrice(price) {
      if (!this.productPrice) return;
      this.productPrice.textContent = price;
    }

    updateProductTitle(title) {
      if (!this.productTitle) return;
      this.productTitle.textContent = title;
    }

    updateProductLink(link) {
      if (this.productMediaHolder) {
        this.productMediaHolder.setAttribute('href', link);
        this.productMediaHolder.setAttribute('aria-label', this.productTitle?.textContent || '');
      }
      if (this.productTitle) this.productTitle.setAttribute('href', link);
      if (this.productPrice) this.productPrice.setAttribute('href', link);
    }

    updateProductImage(image) {
      if (!this.productImage || !image) return;
      this.productImage.setAttribute('src', image);
      this.productImage.setAttribute('srcset', image);
      this.productImage.setAttribute('alt', this.productTitle ? this.productTitle.textContent : '');
    }

    updateProductHoverImage(image) {
      const hover = this.productItem.querySelector('hover-images');
      if (!hover || !image) return;
      const featured = this.productImage?.getAttribute('src') || image;
      const mediaKey = featured + '|' + image;
      if (this.hoverMediaKey === mediaKey) return;
      this.hoverMediaKey = mediaKey;
      const replacement = document.createElement('hover-images');
      replacement.className = hover.className;
      const slider = document.createElement('div');
      slider.className = 'product-media__slider';
      slider.setAttribute('data-hover-slider', '');
      [featured, image].forEach((src, index) => {
        const slide = document.createElement('div');
        slide.className = 'product-media__slide';
        slide.setAttribute('data-hover-slide-touch', '');
        if (index) slide.setAttribute('data-hover-slide', '');
        const img = document.createElement('img');
        img.src = src;
        img.alt = this.productTitle?.textContent || '';
        img.style.cssText = 'width:100%;height:100%;object-fit:' + (window.theme.settings.gridImageSize === 'contain' ? 'contain' : 'cover');
        slide.appendChild(img);
        slider.appendChild(slide);
      });
      replacement.appendChild(slider);
      hover.replaceWith(replacement);
    }

    updateQuickViewBtn(handle) {
      if (!this.quickViewBtn) return;
      this.quickViewBtn.setAttribute('data-handle', handle);
      this.quickViewBtn.setAttribute('href', this.activeSwatch.getAttribute('href'));
    }
  }

  if (!customElements.get('product-grid-item-linked')) {
    customElements.define('product-grid-item-linked', ProductGridItemLinked);
  }

  // ============================================================
  // Vertical product thumbnails — desktop left-side layout
  //
  // custom.js is loaded as `defer` and executes AFTER vendor.js,
  // theme.js and product-info.js (all also defer, loaded earlier).
  // By the time this code runs, Flickity is already initialised on
  // every product-info element on the page, so we can act immediately
  // without waiting for DOMContentLoaded.
  // ============================================================
  (function () {
    var DESKTOP_MIN = 768;

    // Some mobile browsers expose a desktop-sized layout viewport.  A width
    // check alone can therefore apply the desktop thumbnail layout on a phone.
    // Require a mouse-style pointer and hover capability as well.
    function usesDesktopThumbnailLayout() {
      return window.matchMedia(
        '(min-width: ' + DESKTOP_MIN + 'px) and (hover: hover) and (pointer: fine)'
      ).matches;
    }

    function applyVertical(productInfo) {
      if (!usesDesktopThumbnailLayout()) return false;

      var thumbContainer = productInfo.querySelector('[data-product-single-media-thumbs]');
      var mainSlider     = productInfo.querySelector('[data-product-single-media-slider]');
      if (!thumbContainer || !mainSlider) return false;

      var F = window.theme && window.theme.Flickity;
      if (!F) return false;

      var navFlkty  = F.data(thumbContainer);
      var mainFlkty = F.data(mainSlider);
      if (!navFlkty || !mainFlkty) return false;

      // Guard: already handled this exact Flickity instance
      if (thumbContainer._vFlkty === navFlkty) return true;
      if (thumbContainer._vHeightCleanup) thumbContainer._vHeightCleanup();
      thumbContainer._vFlkty = navFlkty;

      // Locate the gallery wrapper
      var gallery = thumbContainer.closest('[data-product-single-media-group]');

      // Null out product-info's own flktyNav reference so its
      // resizeFlickityNav() handler won't crash after we destroy it
      var piEl = thumbContainer.closest('product-info');
      if (piEl) piEl.flktyNav = null;

      // Destroy nav Flickity FIRST, then add layout classes
      // (order matters: Flickity must be gone before flex layout applies)
      navFlkty.destroy();

      if (gallery) gallery.classList.add('gallery--v-thumbs');
      thumbContainer.classList.add('v-thumbs-ready');

      // Force main slider to recalculate its width in the new flex container
      requestAnimationFrame(function () {
        mainFlkty.resize();
        syncHeight();
      });

      // Keep thumbnail column height in sync with the selected media.
      function syncHeight() {
        var selected = mainFlkty.selectedElement;
        var media = selected && selected.querySelector('.product-gallery__media, [data-deferred-media]');
        var h = media ? media.getBoundingClientRect().height : 0;
        if (gallery && h && thumbContainer.classList.contains('v-thumbs-ready')) {
          gallery.style.setProperty('--thumb-col-height', h + 'px');
        }
      }
      mainFlkty.on('resize', syncHeight);
      mainFlkty.on('change', syncHeight);
      mainFlkty.on('settle', syncHeight);
      mainSlider.addEventListener('load', syncHeight, true);
      var heightObserver = new ResizeObserver(syncHeight);
      mainSlider.querySelectorAll('.product-gallery__media, [data-deferred-media]').forEach(function (media) {
        heightObserver.observe(media);
      });
      thumbContainer._vHeightCleanup = function () {
        mainFlkty.off('resize', syncHeight);
        mainFlkty.off('change', syncHeight);
        mainFlkty.off('settle', syncHeight);
        mainSlider.removeEventListener('load', syncHeight, true);
        heightObserver.disconnect();
        thumbContainer._vHeightCleanup = null;
      };

      // Click: navigate the main slider
      // Both .product-single__thumbnail and slide wrappers carry
      // data-id="{{ media.id }}" (plain numeric) — safe to compare.
      var thumbs = Array.from(thumbContainer.querySelectorAll('.product-single__thumbnail'));
      thumbs.forEach(function (thumb) {
        if (thumb._vClickBound) return;
        thumb._vClickBound = true;
        thumb.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var mediaId  = thumb.getAttribute('data-id');
          var slides   = Array.from(mainSlider.querySelectorAll('[data-product-single-media-wrapper]'));
          var idx      = slides.findIndex(function (s) { return s.getAttribute('data-id') == mediaId; });
          var curFlkty = F.data(mainSlider);
          if (idx >= 0 && curFlkty) curFlkty.select(idx);
        }, true); // capture phase fires before product-info.js bubble listeners
      });

      // Sync is-nav-selected highlight with the main slider
      function syncSelected() {
        var curFlkty = F.data(mainSlider);
        if (!curFlkty) return;
        
        var cells = curFlkty.cells;
        if (!cells || !cells.length) return;
        
        var selectedCell = cells[curFlkty.selectedIndex];
        if (!selectedCell || !selectedCell.element) return;

        var selId = selectedCell.element.getAttribute('data-id');
        
        thumbs.forEach(function (t) {
          var isMatch = (t.getAttribute('data-id') == selId);
          t.classList.toggle('is-nav-selected', isMatch);
          t.classList.toggle('active-thumb-pc', isMatch); // extra class for our forced CSS
        });
      }
      syncSelected();
      mainFlkty.on('change', syncSelected);

      return true;
    }

    function removeVertical(productInfo) {
      var thumbContainer = productInfo.querySelector('[data-product-single-media-thumbs]');
      var gallery        = productInfo.querySelector('[data-product-single-media-group]');
      if (thumbContainer) {
        if (thumbContainer._vHeightCleanup) thumbContainer._vHeightCleanup();
        thumbContainer.classList.remove('v-thumbs-ready');
        thumbContainer._vFlkty = null;
      }
      if (gallery) {
        gallery.classList.remove('gallery--v-thumbs');
        gallery.style.removeProperty('--thumb-col-height');
      }
    }

    function pollUntilReady(productInfo, attempts) {
      if (!usesDesktopThumbnailLayout()) return;
      attempts = attempts || 0;
      if (applyVertical(productInfo)) return;
      if (attempts < 40) {
        setTimeout(function () { pollUntilReady(productInfo, attempts + 1); }, 150);
      }
    }

    // Run immediately — product-info.js (loaded earlier via defer)
    // has already initialised Flickity by the time we reach this line.
    document.querySelectorAll('product-info').forEach(function (pi) {
      applyVertical(pi) || pollUntilReady(pi);
    });

    // theme:product-info:loaded fires at the START of connectedCallback(),
    // before Flickity is initialised → must poll for it.
    document.addEventListener('theme:product-info:loaded', function (e) {
      pollUntilReady(e.target);
    });

    // product-info.js re-creates Flickity nav on every theme:resize:width,
    // so we re-intercept it 600 ms later (after its 400 ms debounce).
    // On mobile: removeVertical MUST fire immediately (before product-info.js
    // re-inits Flickity ~400 ms later), otherwise Flickity is created while
    // v-thumbs-ready flex CSS is still active and the carousel breaks.
    document.addEventListener('theme:resize:width', function () {
      if (!usesDesktopThumbnailLayout()) {
        document.querySelectorAll('product-info').forEach(removeVertical);
      } else {
        setTimeout(function () {
          document.querySelectorAll('product-info').forEach(function (pi) {
            applyVertical(pi) || pollUntilReady(pi);
          });
        }, 600);
      }
    });

    // Shopify theme-editor section reload
    document.addEventListener('shopify:section:load', function () {
      setTimeout(function () {
        document.querySelectorAll('product-info').forEach(function (pi) { pollUntilReady(pi); });
      }, 200);
    });
  }());

  (function rememberProductSize() {
    var storageKey = 'cicybell.preferredSize';
    var initializedProducts = new WeakSet();
    var restoring = false;

    document.addEventListener('change', function (event) {
      var input = event.target;
      var product = input.closest('product-info');
      if (restoring || !product || !input.matches('[data-single-option-selector]') || !input.closest('[data-size-option]')) return;
      initializedProducts.add(product);
      try {
        localStorage.setItem(storageKey, input.value);
      } catch (error) {}
    });

    function restoreSize(product) {
      if (!product.isConnected || initializedProducts.has(product)) return;
      initializedProducts.add(product);
      var size;
      try {
        size = localStorage.getItem(storageKey);
      } catch (error) {
        return;
      }
      if (!size) return;

      var group = product.querySelector('variant-selects [data-size-option]');
      if (!group) return;
      var selected = group.querySelector('input:checked, [data-popout-input]');
      if (selected && selected.value === size) return;
      var radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(function (input) {
        return input.value === size && !input.disabled && !input.closest('.sold-out');
      });
      var option = Array.from(group.querySelectorAll('[data-popout-option]')).find(function (link) {
        return link.dataset.value === size && !link.classList.contains('unavailable');
      });
      restoring = true;
      try {
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (option) {
          option.click();
        }
      } finally {
        restoring = false;
      }
    }

    var ready = Promise.all([
      customElements.whenDefined('product-info'),
      customElements.whenDefined('variant-selects'),
      customElements.whenDefined('popout-select')
    ]);

    function scheduleRestore(product) {
      ready.then(function () {
        requestAnimationFrame(function () { restoreSize(product); });
      });
    }

    document.addEventListener('theme:product-info:loaded', function (event) {
      scheduleRestore(event.target);
    });
    document.querySelectorAll('product-info').forEach(scheduleRestore);
  }());

  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.
})();
